import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Spinner } from '@blueprintjs/core';
import API from '../Utilities/API';

// AI endpoint for style transformation
const AI_ENDPOINT = 'http://172.22.7.200:3333/generate'; // Replace with actual endpoint

const AIEditor = ({ image, case_id, reload }) => {
    const [selectedStyle, setSelectedStyle] = useState(null);
    const [currentImage, setCurrentImage] = useState(image);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [styles, setStyles] = useState([]); // Style definitions from API
    const [availableStyles, setAvailableStyles] = useState({}); // Pre-generated styles: { style_id: { file_id, image_path } }
    const [isLoadingStyles, setIsLoadingStyles] = useState(false);
    const [isAddingToCase, setIsAddingToCase] = useState(false);
    const [addToCaseSuccess, setAddToCaseSuccess] = useState(null); // null, true, or error message

    // Fetch available styles and pre-generated images from API
    const fetchAvailableStyles = useCallback(async () => {
        if (!image) return;

        setIsLoadingStyles(true);
        try {
            const response = await API.GetImageStyles(image);
            console.log('available styles', response?.data?.Data);
            if (response?.data?.Data) {
                const { styles: apiStyles, image: imageStyles } = response.data.Data;
                // Set style definitions from API
                if (apiStyles && Array.isArray(apiStyles)) {
                    setStyles(apiStyles);
                }
                // Set pre-generated image styles
                // API returns array of { file_id, image_path, style_id } objects, convert to map
                // Result: { style_id: { file_id, image_path } }
                if (imageStyles) {
                    const stylesMap = {};
                    Object.keys(imageStyles).forEach((styleId) => {
                        const item = imageStyles[styleId];
                        stylesMap[styleId] = {
                            file_id: item.upload_id || item.file_id,
                            image_path: item.image_path
                        };
                    });
                    console.log(stylesMap);
                    setAvailableStyles(stylesMap);
                }
            }
        } catch (err) {
            console.error('Error fetching available styles:', err);
        } finally {
            setIsLoadingStyles(false);
        }
    }, [image]);

    // Fetch styles on first load
    useEffect(() => {
        fetchAvailableStyles();
    }, [fetchAvailableStyles]);

    const applyStyle = async (style) => {
        setSelectedStyle(style);
        setError(null);
        setIsLoading(true);

        try {
            const response = await fetch(AI_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    images: [image], // Always use original image, not currentImage
                    filter_id: style.id
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success && data.image) {
                // Update the image with the base64 data
                setCurrentImage(`data:image/png;base64,${data.image}`);
                // Store the upload_id if returned from AI
                if (data.upload_id) {
                    setAvailableStyles(prev => ({
                        ...prev,
                        [style.id]: {
                            ...prev[style.id],
                            file_id: data.upload_id
                        }
                    }));
                }
                // Reset add to case status
                setAddToCaseSuccess(null);
                // Reload available styles after AI finishes processing
                fetchAvailableStyles();
            } else {
                throw new Error('Invalid response from AI service');
            }
        } catch (err) {
            console.error('Error applying style:', err);
            setError(err.message || 'Failed to apply style');
        } finally {
            setIsLoading(false);
        }
    };

    const handleStyleClick = async (style) => {
        // Reset add to case status when changing styles
        setAddToCaseSuccess(null);

        // Handle "Original" style - just reset to original image
        if (style.id === 'original') {
            setSelectedStyle(style);
            setCurrentImage(image);
            setError(null);
            return;
        }

        // Check if there's a pre-generated URL available for this style
        const preGenerated = availableStyles[style.id];

        if (preGenerated?.image_path) {
            // Use pre-generated image directly without calling AI
            setSelectedStyle(style);
            setCurrentImage(preGenerated.image_path);
            setError(null);
            return;
        }

        // No pre-generated image available, proceed with AI generation
        await applyStyle(style);
    };

    // Refresh a style - regenerate even if pre-generated version exists
    const handleRefreshStyle = async (style) => {
        if (!style || style.id === 'original') return;
        await applyStyle(style);
    };

    // Add the generated image to the case
    const handleAddToCase = async () => {
        if (!selectedStyle || selectedStyle.id === 'original' || !case_id) return;

        const upload_id = availableStyles[selectedStyle.id]?.file_id;
        if (!upload_id) {
            setAddToCaseSuccess('No upload ID available for this image');
            return;
        }

        setIsAddingToCase(true);
        setAddToCaseSuccess(null);

        try {
            const title = `AI Generated - ${selectedStyle.name || 'Styled Image'}`;
            const response = await API.AddUploadToCase(case_id, upload_id, title);

            if (response?.data?.Error) {
                throw new Error(response.data.Error);
            }

            setAddToCaseSuccess(true);
        } catch (err) {
            console.error('Error adding to case:', err);
            setAddToCaseSuccess(err.message || 'Failed to add image to case');
        } finally {
            setIsAddingToCase(false);
        }
    };

    return (
        <div style={{
            display: 'flex',
            height: '100%',
            width: '100%',
            backgroundColor: '#30404d'
        }}>
            {/* Left sidebar - Style thumbnails */}
            <div style={{
                width: '200px',
                height: '100%',
                overflowY: 'auto',
                padding: '10px',
                borderRight: '1px solid #394b59'
            }}>
                <h3 style={{
                    marginTop: 0,
                    marginBottom: 15,
                    color: '#f5f8fa',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    Styles
                    {isLoadingStyles && <Spinner size={14} />}
                </h3>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                }}>
                    {styles.map((style, index) => {
                        // For "Original" style, use the original image as thumbnail
                        // For other styles, use pre-generated URL if available, otherwise use default thumbnail
                        const thumbnailUrl = style.id === 'original'
                            ? image
                            : (availableStyles[style.id]?.image_path || style.thumbnail);
                        const hasPreGenerated = style.id !== 'original' && !!availableStyles[style.id]?.image_path;

                        return (
                            <Card
                                key={style.id || index}
                                interactive
                                onClick={() => handleStyleClick(style)}
                                style={{
                                    padding: 0,
                                    cursor: 'pointer',
                                    border: selectedStyle?.id === style.id ? '2px solid #2D72D2' : '2px solid transparent',
                                    transition: 'border 0.2s'
                                }}
                            >
                                <div style={{
                                    width: '100%',
                                    paddingTop: '100%',
                                    backgroundImage: `url(${thumbnailUrl})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundColor: '#202b33',
                                    position: 'relative'
                                }}>
                                    {style.name && (
                                        <div style={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                            color: '#f5f8fa',
                                            padding: '5px',
                                            fontSize: '0.8rem',
                                            textAlign: 'center',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '5px'
                                        }}>
                                            {style.name}
                                            {hasPreGenerated && (
                                                <span style={{
                                                    width: '8px',
                                                    height: '8px',
                                                    backgroundColor: '#15b371',
                                                    borderRadius: '50%',
                                                    display: 'inline-block'
                                                }} title="Pre-generated preview available" />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </Card>
                        );
                    })}
                </div>
            </div>

            {/* Center area - Main image */}
            <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                position: 'relative'
            }}>
                {currentImage ? (
                    <img
                        src={currentImage}
                        alt="Main content"
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            opacity: isLoading ? 0.5 : 1,
                            transition: 'opacity 0.3s'
                        }}
                    />
                ) : (
                    <div style={{
                        color: '#f5f8fa',
                        fontSize: '1.2rem',
                        textAlign: 'center'
                    }}>
                        No image provided
                    </div>
                )}

                {/* Loading overlay */}
                {isLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(48, 64, 77, 0.8)',
                        zIndex: 10
                    }}>
                        <Spinner size={60} />
                        <div style={{
                            color: '#f5f8fa',
                            marginTop: '20px',
                            fontSize: '1rem'
                        }}>
                            Applying style...
                        </div>
                    </div>
                )}

                {/* Error message */}
                {error && (
                    <div style={{
                        position: 'absolute',
                        bottom: 20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#db3737',
                        padding: '10px 15px',
                        borderRadius: '3px',
                        color: '#fff',
                        boxShadow: '0 0 0 1px rgba(16,22,26,.4)',
                        maxWidth: '80%',
                        textAlign: 'center'
                    }}>
                        Error: {error}
                    </div>
                )}

                {/* Optional: Display selected style name with refresh and add to case buttons */}
                {selectedStyle && !isLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 20,
                        right: 20,
                        backgroundColor: '#394b59',
                        padding: '10px 15px',
                        borderRadius: '3px',
                        color: '#f5f8fa',
                        boxShadow: '0 0 0 1px rgba(16,22,26,.4)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <span>Style: {selectedStyle.name || 'Unnamed'}</span>
                        {selectedStyle.id !== 'original' && availableStyles[selectedStyle.id]?.image_path && (
                            <>
                                <Button
                                    icon="refresh"
                                    small
                                    minimal
                                    intent="primary"
                                    onClick={() => handleRefreshStyle(selectedStyle)}
                                    title="Regenerate this style"
                                />
                                {case_id && availableStyles[selectedStyle.id]?.file_id && (
                                    <Button
                                        icon="add"
                                        small
                                        intent="success"
                                        loading={isAddingToCase}
                                        disabled={addToCaseSuccess === true}
                                        onClick={handleAddToCase}
                                        title="Add this image to case"
                                    >
                                        {addToCaseSuccess === true ? 'Added' : 'Add to Case'}
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Success/Error message for adding to case */}
                {addToCaseSuccess !== null && (
                    <div style={{
                        position: 'absolute',
                        top: 70,
                        right: 20,
                        backgroundColor: addToCaseSuccess === true ? '#0d8050' : '#db3737',
                        padding: '8px 12px',
                        borderRadius: '3px',
                        color: '#fff',
                        boxShadow: '0 0 0 1px rgba(16,22,26,.4)',
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        {addToCaseSuccess === true
                            ? 'Image successfully added to case!'
                            : `Error: ${addToCaseSuccess}`}
                        {addToCaseSuccess === true && reload && (
                            <Button
                                icon="refresh"
                                small
                                minimal
                                style={{ color: '#fff' }}
                                onClick={() => reload()}
                            >
                                Reload sources
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AIEditor;
